"use client";

import { CheckIcon, ChevronDownIcon } from "lucide-react";
import { DropdownMenu } from "radix-ui";
import type { FC } from "react";
import { cn } from "@/lib/utils";
import { useModelSelectorEfforts } from "./model-selector";

/** What each level means, under its name in the menu. */
const EFFORT_HINTS: Record<string, string> = {
  low: "Quick answers, little thinking",
  medium: "Thinks when it helps",
  high: "Thinks longest, for hard problems",
};

/**
 * The reasoning effort dropdown beside the model picker (ADMIN-212). It reads and sets the
 * effort held by `ModelSelectorRoot`, so it must sit inside it, and it hides itself for models
 * without effort levels. The choice sticks across model switches.
 */
export const EffortSelector: FC<{ className?: string }> = ({ className }) => {
  const { efforts, effort, setEffort } = useModelSelectorEfforts();
  if (!efforts?.length) return null;
  const current = efforts.find((e) => e.id === effort);

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger
        data-slot="effort-selector-trigger"
        aria-label={`Effort: ${current?.name ?? "default"}`}
        className={cn(
          "focus-visible:ring-ring/50 text-muted-foreground hover:bg-accent hover:text-accent-foreground data-[state=open]:bg-accent flex h-7 shrink-0 items-center gap-1 rounded-full px-2.5 text-xs whitespace-nowrap transition-colors outline-none focus-visible:ring-2",
          className,
        )}
      >
        <span>{current?.name ?? "Effort"}</span>
        <ChevronDownIcon className="size-3.5 opacity-50" />
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="start"
          sideOffset={6}
          className="bg-popover/95 text-popover-foreground data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95 z-50 w-64 overflow-hidden rounded-xl border p-1.5 shadow-lg backdrop-blur-sm"
        >
          <DropdownMenu.Label className="text-muted-foreground px-2.5 pt-1 pb-1.5 text-xs">
            Effort
          </DropdownMenu.Label>
          <DropdownMenu.RadioGroup value={effort ?? ""} onValueChange={setEffort}>
            {efforts.map((option) => (
              <DropdownMenu.RadioItem
                key={option.id}
                value={option.id}
                className="focus:bg-accent focus:text-accent-foreground relative flex cursor-pointer flex-col rounded-lg py-1.5 ps-2.5 pe-8 text-sm outline-none select-none"
              >
                <span className="font-medium">{option.name}</span>
                {EFFORT_HINTS[option.id] && (
                  <span className="text-muted-foreground text-xs">{EFFORT_HINTS[option.id]}</span>
                )}
                <DropdownMenu.ItemIndicator className="absolute end-2.5 top-1/2 -translate-y-1/2">
                  <CheckIcon className="size-4" />
                </DropdownMenu.ItemIndicator>
              </DropdownMenu.RadioItem>
            ))}
          </DropdownMenu.RadioGroup>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
};
